import { useState, useRef, useEffect, useMemo } from "react";
import { CombinedTable, StrollerPolicy, Table as TableType } from "@/types/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Minus, Plus, Trash2, RotateCcw, ArrowUpDown, ArrowUp, ArrowDown, Link2, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

type SortField = 'name' | 'capacity' | null;
type SortDirection = 'asc' | 'desc';

export interface CombinedTableListViewProps {
  combinedTables: CombinedTable[];
  allTables: TableType[];
  onToggleActive: (id: string) => void;
  onToggleBookable?: (id: string) => void;
  onDelete: (id: string) => void;
  onRecalculate: (id: string) => void;
  onUpdateCapacity?: (id: string, newCapacity: number) => Promise<void>;
  onUpdateName?: (id: string, newName: string) => Promise<void>;
  onUpdateAllowsChildren?: (id: string, value: boolean) => Promise<void>;
  onUpdateAllowsStroller?: (id: string, policy: StrollerPolicy) => Promise<void>;
  onUpdateIsTerrace?: (id: string, value: boolean) => Promise<void>;
  onUpdateMinCapacity?: (id: string, newMinCapacity: number) => Promise<void>;
  onUpdateAssignmentPriority?: (id: string, newPriority: number) => Promise<void>;
  showChildrenColumn?: boolean;
  showStrollerColumn?: boolean;
  showTerraceColumn?: boolean;
  showPriorityColumn?: boolean;
  onHover?: (id: string | null) => void;
  onReorderPriorities?: (orderedIds: string[]) => Promise<void>;
}

interface EditingState {
  tableId: string | null;
  field: 'name' | 'capacity' | null;
}

// Sortable row component
function SortableCombinedRow({
  id,
  isDragEnabled,
  children,
  className,
  onMouseEnter,
  onMouseLeave,
}: {
  id: string;
  isDragEnabled: boolean;
  children: React.ReactNode;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isDragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isDragEnabled && (
        <TableCell className="w-[40px] px-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground hover:text-foreground touch-none"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </TableCell>
      )}
      {children}
    </TableRow>
  );
}

export function CombinedTableListView({
  combinedTables,
  allTables,
  onToggleActive,
  onToggleBookable,
  onDelete,
  onRecalculate,
  onUpdateCapacity,
  onUpdateName,
  onUpdateAllowsChildren,
  onUpdateAllowsStroller,
  onUpdateIsTerrace,
  onUpdateMinCapacity,
  onUpdateAssignmentPriority,
  showChildrenColumn = false,
  showStrollerColumn = false,
  showTerraceColumn = false,
  showPriorityColumn = false,
  onHover,
  onReorderPriorities,
}: CombinedTableListViewProps) {
  const [editing, setEditing] = useState<EditingState>({ tableId: null, field: null });
  const [editValue, setEditValue] = useState<string>("");
  const [saving, setSaving] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const inputRef = useRef<HTMLInputElement>(null);

  const isDragEnabled = showPriorityColumn && sortField === null && !!onReorderPriorities;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Natural sort function for alphanumeric strings
  const naturalCompare = (a: string, b: string): number => {
    return a.localeCompare(b, 'fr', { numeric: true, sensitivity: 'base' });
  };

  // Sort combined tables
  const sortedTables = useMemo(() => {
    if (sortField) {
      return [...combinedTables].sort((a, b) => {
        let comparison = 0;
        if (sortField === 'name') {
          comparison = naturalCompare(a.name, b.name);
        } else if (sortField === 'capacity') {
          comparison = a.totalCapacity - b.totalCapacity;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    if (showPriorityColumn) {
      return [...combinedTables].sort((a, b) => (a.assignmentPriority ?? 0) - (b.assignmentPriority ?? 0));
    }
    return combinedTables;
  }, [combinedTables, sortField, sortDirection, showPriorityColumn]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  useEffect(() => {
    if (editing.tableId && editing.field === 'name' && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = (table: CombinedTable, field: 'name' | 'capacity') => {
    if (field === 'name' && !onUpdateName) return;
    if (field === 'capacity' && !onUpdateCapacity) return;
    setEditing({ tableId: table.id, field });
    setEditValue(field === 'name' ? table.name : table.totalCapacity.toString());
  };

  const cancelEditing = () => {
    setEditing({ tableId: null, field: null });
    setEditValue("");
  };

  const saveEdit = async (table: CombinedTable) => {
    if (!editing.field) return;

    if (editing.field === 'name') {
      const newName = editValue.trim();
      if (!newName || newName === table.name) {
        cancelEditing();
        return;
      }
      if (!onUpdateName) return;

      setSaving(table.id);
      try {
        await onUpdateName(table.id, newName);
      } finally {
        setSaving(null);
        cancelEditing();
      }
    } else if (editing.field === 'capacity') {
      const newCapacity = parseInt(editValue);
      if (isNaN(newCapacity) || newCapacity < 1 || newCapacity === table.totalCapacity) {
        cancelEditing();
        return;
      }
      if (!onUpdateCapacity) return;

      setSaving(table.id);
      try {
        await onUpdateCapacity(table.id, newCapacity);
      } finally {
        setSaving(null);
        cancelEditing();
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, table: CombinedTable) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit(table);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleCapacityChange = async (table: CombinedTable, delta: number) => {
    if (!onUpdateCapacity) return;
    const newCapacity = Math.max(1, table.totalCapacity + delta);
    if (newCapacity !== table.totalCapacity) {
      setSaving(table.id);
      try {
        await onUpdateCapacity(table.id, newCapacity);
      } finally {
        setSaving(null);
      }
    }
  };

  // Helper to get component tables for a combined table
  const getComponentTables = (combinedTable: CombinedTable): TableType[] => {
    return allTables.filter(t => combinedTable.tableIds.includes(t.id));
  };

  // Calculate physical capacity (sum of component tables)
  const getPhysicalCapacity = (combinedTable: CombinedTable): number => {
    const components = getComponentTables(combinedTable);
    return components.reduce((sum, t) => sum + t.capacity, 0);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !onReorderPriorities) return;

    const oldIndex = sortedTables.findIndex(t => t.id === active.id);
    const newIndex = sortedTables.findIndex(t => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedTables, oldIndex, newIndex);
    onReorderPriorities(reordered.map(t => t.id));
  };

  const tableContent = (
    <Table>
      <TableHeader>
        <TableRow>
          {isDragEnabled && <TableHead className="w-[40px]"></TableHead>}
          <TableHead className="w-[200px]">
            <button
              onClick={() => handleSort('name')}
              className="flex items-center hover:text-foreground transition-colors"
            >
              Nom
              {getSortIcon('name')}
            </button>
          </TableHead>
          <TableHead className="w-[200px]">Tables composantes</TableHead>
          <TableHead className="w-[140px]">
            <button
              onClick={() => handleSort('capacity')}
              className="flex items-center hover:text-foreground transition-colors"
            >
              Capacité (max)
              {getSortIcon('capacity')}
            </button>
          </TableHead>
          <TableHead className="w-[140px]">
            <span className="flex items-center">Capacité (min)</span>
          </TableHead>
          <TableHead className="w-[100px] text-center">Réservable</TableHead>
          {showChildrenColumn && (
            <TableHead className="w-[100px] text-center">Enfants</TableHead>
          )}
          {showStrollerColumn && (
            <TableHead className="w-[100px] text-center">Poussettes</TableHead>
          )}
          {showTerraceColumn && (
            <TableHead className="w-[100px] text-center">Terrasse</TableHead>
          )}
          {showPriorityColumn && (
            <TableHead className="w-[120px] text-center">Priorité</TableHead>
          )}
          <TableHead className="w-[100px] text-center">Active</TableHead>
          <TableHead className="w-[60px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedTables.map((combinedTable) => {
          const isEditingName = editing.tableId === combinedTable.id && editing.field === 'name';
          const isEditingCapacity = editing.tableId === combinedTable.id && editing.field === 'capacity';
          const isSaving = saving === combinedTable.id;
          const componentTables = getComponentTables(combinedTable);
          const physicalCapacity = getPhysicalCapacity(combinedTable);
          const hasCustomCapacity = combinedTable.totalCapacity !== physicalCapacity;

          return (
            <SortableCombinedRow
              key={combinedTable.id}
              id={combinedTable.id}
              isDragEnabled={isDragEnabled}
              className={isSaving ? "opacity-70" : ""}
              onMouseEnter={() => onHover?.(combinedTable.id)}
              onMouseLeave={() => onHover?.(null)}
            >
              {/* Name Column */}
              <TableCell>
                {isEditingName ? (
                  <div className="flex items-center gap-1">
                    <Input
                      ref={inputRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, combinedTable)}
                      onBlur={() => saveEdit(combinedTable)}
                      className="h-8 w-full"
                      disabled={isSaving}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => onUpdateName && startEditing(combinedTable, 'name')}
                    className={`text-left w-full font-medium flex items-center gap-2 ${onUpdateName ? 'hover:text-primary transition-colors' : ''}`}
                    disabled={!onUpdateName}
                  >
                    <Link2 className="w-4 h-4 text-primary flex-shrink-0" />
                    {combinedTable.name}
                  </button>
                )}
              </TableCell>

              {/* Component Tables Column */}
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {componentTables.map((table) => {
                    const isNonBookable = table.isActive && table.isBookable === false;
                    const badge = (
                      <Badge
                        key={table.id}
                        variant="outline"
                        className={`text-xs ${!table.isActive ? "opacity-50 line-through" : ""} ${isNonBookable ? "border-amber-400 text-amber-700 bg-amber-50 cursor-help" : ""}`}
                      >
                        {table.name}{isNonBookable ? " (HR)" : ""}
                      </Badge>
                    );
                    return isNonBookable ? (
                      <TooltipProvider key={table.id}>
                        <Tooltip>
                          <TooltipTrigger asChild>{badge}</TooltipTrigger>
                          <TooltipContent>
                            <p>{table.name} est marquée hors réservation</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : badge;
                  })}
                </div>
              </TableCell>

              {/* Capacity Column */}
              <TableCell>
                {isEditingCapacity ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, combinedTable)}
                      onBlur={() => saveEdit(combinedTable)}
                      className="h-8 w-16 text-center"
                      min={1}
                      disabled={isSaving}
                      autoFocus
                    />
                    <span className="text-muted-foreground text-sm">pers.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1">
                    {onUpdateCapacity && (
                       <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => handleCapacityChange(combinedTable, -1)}
                        disabled={combinedTable.totalCapacity <= 1 || isSaving}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                    )}
                    <button
                      onClick={() => onUpdateCapacity && startEditing(combinedTable, 'capacity')}
                      className={`min-w-[40px] text-center font-medium ${onUpdateCapacity ? 'hover:text-primary transition-colors' : ''}`}
                      disabled={!onUpdateCapacity}
                    >
                      {combinedTable.totalCapacity}
                    </button>
                    {onUpdateCapacity && (
                       <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => handleCapacityChange(combinedTable, 1)}
                        disabled={isSaving}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                    {hasCustomCapacity && (
                      <span className="text-xs text-muted-foreground ml-1">
                        (max: {physicalCapacity})
                      </span>
                    )}
                  </div>
                )}
              </TableCell>

              {/* Min Capacity Column */}
              <TableCell>
                <div className="flex items-center gap-1">
                  {onUpdateMinCapacity && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => {
                        const newMin = Math.max(1, (combinedTable.minCapacity ?? 1) - 1);
                        onUpdateMinCapacity(combinedTable.id, newMin);
                      }}
                      disabled={(combinedTable.minCapacity ?? 1) <= 1 || isSaving}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                  )}
                  <span className="min-w-[40px] text-center font-medium">
                    {combinedTable.minCapacity ?? 1}
                  </span>
                  {onUpdateMinCapacity && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9"
                      onClick={() => {
                        const newMin = Math.min(combinedTable.totalCapacity, (combinedTable.minCapacity ?? 1) + 1);
                        onUpdateMinCapacity(combinedTable.id, newMin);
                      }}
                      disabled={(combinedTable.minCapacity ?? 1) >= combinedTable.totalCapacity || isSaving}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>

              {/* Bookable Column */}
              <TableCell className="text-center">
                <Switch
                  checked={combinedTable.isBookable !== false}
                  onCheckedChange={() => onToggleBookable?.(combinedTable.id)}
                  disabled={isSaving || !onToggleBookable}
                />
              </TableCell>

              {/* Children Column */}
              {showChildrenColumn && (
                <TableCell className="text-center">
                  <Switch
                    checked={combinedTable.allowsChildren !== false}
                    onCheckedChange={(checked) => onUpdateAllowsChildren?.(combinedTable.id, checked)}
                    disabled={isSaving || !onUpdateAllowsChildren}
                  />
                </TableCell>
              )}

              {/* Stroller Column */}
              {showStrollerColumn && (
                <TableCell className="text-center">
                  {(() => {
                    const policy: StrollerPolicy = combinedTable.strollerPolicy ?? (combinedTable.allowsStroller === false ? 'deny' : 'inherit');
                    const refusing = allTables.filter(t => combinedTable.tableIds.includes(t.id) && t.allowsStroller === false);
                    return (
                      <div className="flex flex-col items-center gap-1">
                        <Select
                          value={policy}
                          onValueChange={(v) => onUpdateAllowsStroller?.(combinedTable.id, v as StrollerPolicy)}
                          disabled={isSaving || !onUpdateAllowsStroller}
                        >
                          <SelectTrigger className="h-8 w-[150px] text-xs" aria-label="Poussettes">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="inherit">Suivre les tables</SelectItem>
                            <SelectItem value="allow">Accepter</SelectItem>
                            <SelectItem value="deny">Refuser</SelectItem>
                          </SelectContent>
                        </Select>
                        {policy === 'inherit' && refusing.length > 0 && (
                          <span className="max-w-[160px] text-[11px] leading-tight text-muted-foreground">
                            Refusée car {refusing.map(t => t.name).join(', ')} {refusing.length > 1 ? "refusent" : "refuse"}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
              )}

              {/* Terrace Column */}
              {showTerraceColumn && (
                <TableCell className="text-center">
                  <Switch
                    checked={combinedTable.isTerrace === true}
                    onCheckedChange={(checked) => onUpdateIsTerrace?.(combinedTable.id, checked)}
                    disabled={isSaving || !onUpdateIsTerrace}
                  />
                </TableCell>
              )}

              {/* Priority Column */}
              {showPriorityColumn && (
                <TableCell>
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => onUpdateAssignmentPriority?.(combinedTable.id, Math.max(0, (combinedTable.assignmentPriority ?? 0) - 1))}
                      disabled={isSaving || !onUpdateAssignmentPriority || (combinedTable.assignmentPriority ?? 0) <= 0}
                    >
                      <Minus className="h-3 w-3" />
                    </Button>
                    <span className="w-6 text-center text-sm font-mono">{combinedTable.assignmentPriority ?? 0}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => onUpdateAssignmentPriority?.(combinedTable.id, (combinedTable.assignmentPriority ?? 0) + 1)}
                      disabled={isSaving || !onUpdateAssignmentPriority}
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              )}

              {/* Active Column */}
              <TableCell className="text-center">
                <Switch
                  checked={combinedTable.isActive}
                  onCheckedChange={() => onToggleActive(combinedTable.id)}
                  disabled={isSaving}
                />
              </TableCell>

              {/* Actions Column */}
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onRecalculate(combinedTable.id)}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Réinitialiser capacité
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(combinedTable.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </SortableCombinedRow>
          );
        })}
      </TableBody>
    </Table>
  );

  return (
    <div className="border rounded-lg">
      {isDragEnabled ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortedTables.map(t => t.id)}
            strategy={verticalListSortingStrategy}
          >
            {tableContent}
          </SortableContext>
        </DndContext>
      ) : (
        tableContent
      )}
    </div>
  );
}
